import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CategoryManager } from "@/components/shared/CategoryManager";
import { getKnowledgeBase, createKBArticle, updateKBArticle, deleteKBArticle, getKBCategories, createKBCategory, updateKBCategory, deleteKBCategory, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Plus, Pencil, Trash2, Loader2, Search, Settings, ChevronLeft, ChevronRight, ImagePlus, X, Video, Wrench } from "lucide-react";

const EMPTY_PAGE = { content: "", image_url: "", video_url: "" };

function KBPageEditor({ pages, onChange }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [uploading, setUploading] = useState(false);

  const addPage = () => { onChange([...pages, { ...EMPTY_PAGE }]); setCurrentPage(pages.length); };
  const removePage = (idx) => {
    const next = pages.filter((_, i) => i !== idx);
    onChange(next);
    setCurrentPage(Math.max(0, Math.min(currentPage, next.length - 1)));
  };
  const updatePage = (field, value) => {
    const next = [...pages];
    next[currentPage] = { ...next[currentPage], [field]: value };
    onChange(next);
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file, "kb-pages");
      updatePage("image_url", result.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const page = pages[currentPage] || {};

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="kb-page-editor">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-navy">Article Pages ({pages.length})</h4>
        <Button type="button" size="sm" variant="outline" onClick={addPage} data-testid="add-kb-page-btn"><Plus className="w-3 h-3 mr-1" /> Add Page</Button>
      </div>
      {pages.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No pages. Add pages for slideshow-style content, or use the main content field for simple articles.</p>
      ) : (
        <>
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-navy">Page {currentPage + 1} of {pages.length}</span>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(currentPage + 1)}><ChevronRight className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removePage(currentPage)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
          <div><Label>Content</Label><Textarea rows={5} value={page.content || ""} onChange={(e) => updatePage("content", e.target.value)} placeholder="Page content..." data-testid="kb-page-content" /></div>
          <div>
            <Label>Image</Label>
            {page.image_url ? (
              <div className="flex items-center gap-2 mt-1"><img src={page.image_url} alt="" className="w-20 h-20 object-cover rounded border" /><Button type="button" variant="ghost" size="sm" onClick={() => updatePage("image_url", "")}><X className="w-3 h-3" /></Button></div>
            ) : (
              <div className="mt-1">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id={`kb-page-img-${currentPage}`} />
                <label htmlFor={`kb-page-img-${currentPage}`}>
                  <div className="border border-dashed border-slate-200 rounded p-2 text-center cursor-pointer hover:border-teal/50 text-xs text-slate-400">{uploading ? "Uploading..." : <><ImagePlus className="w-4 h-4 inline mr-1" />Upload image</>}</div>
                </label>
              </div>
            )}
          </div>
          <div><Label>Video URL</Label><Input value={page.video_url || ""} onChange={(e) => updatePage("video_url", e.target.value)} placeholder="https://youtube.com/..." data-testid="kb-page-video" /></div>
        </>
      )}
    </div>
  );
}

export default function AdminKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "", is_repair_content: false, pages: [] });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogTab, setDialogTab] = useState("details");

  const fetchData = async () => {
    try {
      const [arts, cats] = await Promise.all([getKnowledgeBase(), getKBCategories()]);
      setArticles(arts);
      setCategories(cats);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", category: categories[0]?.value || "", is_repair_content: false, pages: [] }); setDialogTab("details"); setDialogOpen(true); };
  const openEdit = (article) => { setEditing(article); setForm({ title: article.title, content: article.content || "", category: article.category, is_repair_content: article.is_repair_content || false, pages: article.pages || [] }); setDialogTab("details"); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.title) return toast.error("Title is required");
    setSaving(true);
    try {
      if (editing) { await updateKBArticle(editing.id, form); toast.success("Article updated"); }
      else { await createKBArticle(form); toast.success("Article created"); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error("Failed to save article"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await deleteKBArticle(id); toast.success("Article deleted"); fetchData(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const filtered = articles.filter((a) => search ? (a.title?.toLowerCase().includes(search.toLowerCase()) || a.content?.toLowerCase().includes(search.toLowerCase())) : true);
  const getCatLabel = (val) => categories.find((c) => c.value === val)?.label || val;

  return (
    <AdminLayout title="Knowledge Base" breadcrumb="Knowledge Base">
      <div className="space-y-6" data-testid="admin-kb">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="kb-search" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCatManagerOpen(true)} data-testid="manage-kb-categories-btn"><Settings className="w-4 h-4 mr-2" /> Categories</Button>
            <Button className="bg-teal hover:bg-teal/90" onClick={openCreate} data-testid="create-kb-btn"><Plus className="w-4 h-4 mr-2" /> New Article</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">{search ? "No articles matching search" : "No articles yet"}</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((article) => (
              <Card key={article.id} className="border-slate-200" data-testid={`kb-article-${article.id}`}>
                <CardContent className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-navy text-sm">{article.title}</h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{article.content}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{getCatLabel(article.category)}</span>
                      {article.pages?.length > 0 && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{article.pages.length} pages</span>}
                      {article.is_repair_content && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1"><Wrench className="w-3 h-3" />Repair</span>}
                      <span className="text-xs text-slate-400">{new Date(article.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(article)} data-testid={`edit-kb-${article.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(article.id)} data-testid={`delete-kb-${article.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kb-dialog">
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
            <div className="flex gap-2 mb-4">
              {["details", "pages"].map((t) => (
                <button key={t} onClick={() => setDialogTab(t)} className={`px-3 py-1.5 rounded text-xs font-medium capitalize transition-colors ${dialogTab === t ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`kb-dialog-tab-${t}`}>{t}</button>
              ))}
            </div>

            {dialogTab === "details" && (
              <div className="space-y-4">
                <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-title" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="kb-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Content (main summary)</Label><Textarea rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="kb-content" /></div>
                <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-3">
                  <Switch checked={form.is_repair_content} onCheckedChange={(v) => setForm({ ...form, is_repair_content: v })} data-testid="kb-repair-toggle" />
                  <div><Label className="text-sm text-amber-800 flex items-center gap-1"><Wrench className="w-4 h-4" />Device Repair Content</Label><p className="text-xs text-amber-600">Only visible to dealers in the repair program</p></div>
                </div>
              </div>
            )}

            {dialogTab === "pages" && (
              <KBPageEditor pages={form.pages} onChange={(p) => setForm({ ...form, pages: p })} />
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-kb-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={catManagerOpen} onOpenChange={setCatManagerOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-navy">Manage KB Categories</DialogTitle></DialogHeader>
            <CategoryManager categories={categories} onCreate={async (d) => { await createKBCategory(d); fetchData(); }} onUpdate={async (id, d) => { await updateKBCategory(id, d); fetchData(); }} onDelete={async (id) => { await deleteKBCategory(id); fetchData(); }} />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
